import { useCallback } from 'react'

import useTranslations from '../../../hooks/useTranslations'
import ANTDImage from '../../../shared/antd/ANTDImage'
import ANTDTable from '../../../shared/antd/ANTDTable'
import { dayJs, SAVE_FORMAT } from '../../../utils/dayjs'
import { hubFrontWheel, noImage } from '../../../utils/icons'
import { include, isArray, isEqual } from '../../../utils/javascript'
import { countryLabels } from '../../userManagement/addressData'
import { countryFlagImages, vehicleParts } from '../viewVehicle.description'

const PartDetails = ({ partData = {}, activeMenu, val }) => {
  const { t } = useTranslations()

  // const { ddpData, partInfo } = partData
  const ddpData = {}
  const partInfo = {}

  const getRefurbisherTitle = ({ material }) => {
    switch (material) {
      case 'job_Steel':
        return 'inv_RefurbisherSteel'
      case 'Aluminium':
        return 'inv_RefurbisherAluminium'
      case 'Nickel':
        return 'inv_RefurbisherNickel'
      case 'Copper':
        return 'inv_RefurbisherCopper'
      case 'Plastic':
        return 'inv_RefurbisherPlastic'
      case 'Rubber':
        return 'inv_RefurbisherRubber'

      default:
        return 'user_MaterialProducer'
    }
  }

  const ddpInfoData = [
    { label: 'inv_PartDPPID', value: partData?.dppId },
    {
      label: 'inv_DPPCreated',
      value: partData?.creationDate
        ? dayJs(partData?.creationDate).format(SAVE_FORMAT)
        : '-',
    },
    { label: 'inv_DPPLastUpdated', value: ddpData?.updated },
    {
      label: 'inv_DPPInitiatedBy',
      value: partData?.partProducer?.businessName,
    },
    ...(ddpData?.materialType
      ? [{ label: 'job_MaterialType', value: ddpData?.materialType }]
      : []),
  ]
  const partSubInfoCol2 = [
    [
      {
        label: 'inv_ManufacturingLocation',
        value: partData?.partProducer?.address,
      },
      {
        label: 'inv_Chemistry',
        value: partInfo?.chemistry,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'inv_Dimensions',
        value: partInfo?.dimensions,
        hidden: include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
    ],
    [
      {
        label: 'inv_BatteryCellType',
        value: partInfo?.batteryCell,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'job_Weight',
        value: partData?.scrapQuantity
          ? `${partData?.scrapQuantity} kg`
          : partInfo?.weight,
      },
      {
        label: 'inv_Volume',
        value: partInfo?.volume,
        hidden: include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
    ],
    ...(include([vehicleParts?.battery, vehicleParts?.batteryEV], activeMenu)
      ? [
          [
            {
              label: 'inv_NumberOfCellsPerBattery',
              value: partInfo?.noOfCell,
            },
            { label: 'inv_EnergyDensity', value: partInfo?.energyDensity },
          ],
        ]
      : []),
    [
      {
        label: 'inv_TotalEnergy',
        value: partInfo?.totalEnergy,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'inv_ExpectedLifetime',
        value: partInfo?.expectedLifetime,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'inv_StrengthTensile',
        value: partInfo?.tensileStrength,
        hidden: !include(
          [
            vehicleParts?.body,
            vehicleParts?.hubFrontWheel,
            vehicleParts?.insulatorSubAssyEngineMountLH,
            vehicleParts?.bracketFRDoorBeamFR,
            vehicleParts?.flangeEGRPipe,
            vehicleParts?.plateRadiatorSupport,
            vehicleParts?.bracketWiringHarnessClamp,
            vehicleParts?.holderFuelPressureSensor,
            vehicleParts?.lockSpareWheel,
            vehicleParts?.lidFuelFillerOpeningInner,
            vehicleParts?.innerMidBracket,
            vehicleParts?.pedalBracket,
            vehicleParts?.drumBrake,
            vehicleParts?.bumper,
            vehicleParts?.frontBumper,
            vehicleParts?.backBumper,
            vehicleParts?.engineBlock,
            vehicleParts?.wiringHarness,
            vehicleParts?.evBatteryPack,
          ],
          activeMenu,
        ),
      },
      {
        label: 'inv_CorrosionResistance',
        value: partInfo?.corrosionResistance,
        hidden: !include(
          [
            vehicleParts?.body,
            vehicleParts?.hubFrontWheel,
            vehicleParts?.insulatorSubAssyEngineMountLH,
            vehicleParts?.bracketFRDoorBeamFR,
            vehicleParts?.flangeEGRPipe,
            vehicleParts?.plateRadiatorSupport,
            vehicleParts?.bracketWiringHarnessClamp,
            vehicleParts?.holderFuelPressureSensor,
            vehicleParts?.lockSpareWheel,
            vehicleParts?.lidFuelFillerOpeningInner,
            vehicleParts?.innerMidBracket,
            vehicleParts?.pedalBracket,
            vehicleParts?.drumBrake,
            vehicleParts?.bumper,
            vehicleParts?.frontBumper,
            vehicleParts?.backBumper,
            vehicleParts?.engineBlock,
            vehicleParts?.wiringHarness,
            vehicleParts?.evBatteryPack,
          ],
          activeMenu,
        ),
      },
    ],
    [
      {
        label: 'inv_RatedCapacity',
        value: partInfo?.ratedCapacity,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'inv_RemainingLife',
        value: partInfo?.remainingLifetime,
        hidden: !include(
          [vehicleParts?.battery, vehicleParts?.batteryEV],
          activeMenu,
        ),
      },
      {
        label: 'inv_ImpactResistance',
        value: partInfo?.impactResistance,
        hidden: !include(
          [
            vehicleParts?.body,
            vehicleParts?.hubFrontWheel,
            vehicleParts?.insulatorSubAssyEngineMountLH,
            vehicleParts?.bracketFRDoorBeamFR,
            vehicleParts?.flangeEGRPipe,
            vehicleParts?.plateRadiatorSupport,
            vehicleParts?.bracketWiringHarnessClamp,
            vehicleParts?.holderFuelPressureSensor,
            vehicleParts?.lockSpareWheel,
            vehicleParts?.lidFuelFillerOpeningInner,
            vehicleParts?.innerMidBracket,
            vehicleParts?.pedalBracket,
            vehicleParts?.drumBrake,
            vehicleParts?.bumper,
            vehicleParts?.frontBumper,
            vehicleParts?.backBumper,
            vehicleParts?.engineBlock,
            vehicleParts?.wiringHarness,
            vehicleParts?.evBatteryPack,
          ],
          activeMenu,
        ),
      },
    ],
    ...(include([vehicleParts?.engine], activeMenu)
      ? [
          [
            {
              label: 'inv_ThermalEfficiency',
              value: partInfo?.thermalEfficiency,
            },
            {
              label: 'inv_SpecificFuelConsumption',
              value: partInfo?.specialFuelConsumption,
            },
          ],
          [
            { colSpan: 3 },
            {
              label: 'inv_Torque',
              value: partInfo?.torque,
            },
          ],
        ]
      : []),
    ...(include([vehicleParts?.battery, vehicleParts?.batteryEV], activeMenu)
      ? [
          [
            {
              label: 'inv_VoltageMinNominalMax',
              value: partInfo?.voltage,
            },
            {
              label: 'inv_TemperatureRangeMinMax',
              value: partInfo?.tempRange,
            },
          ],
        ]
      : []),
  ]
  const partSubInfo = [
    [
      {
        label: 'inv_PartStatus',
        value:
          partInfo?.status ||
          include(
            [
              vehicleParts?.frontBumper,
              vehicleParts?.backBumper,
              vehicleParts?.hubFrontWheel,
              vehicleParts?.insulatorSubAssyEngineMountLH,
              vehicleParts?.bracketFRDoorBeamFR,
              vehicleParts?.flangeEGRPipe,
              vehicleParts?.plateRadiatorSupport,
              vehicleParts?.bracketWiringHarnessClamp,
              vehicleParts?.holderFuelPressureSensor,
              vehicleParts?.lockSpareWheel,
              vehicleParts?.lidFuelFillerOpeningInner,
              vehicleParts?.innerMidBracket,
              vehicleParts?.pedalBracket,
            ],
            activeMenu,
          )
            ? 'Original'
            : '',
      },
      {
        label: 'inv_DateOfManufacture',
        value: partData?.creationDate
          ? dayJs(partData?.creationDate).format(SAVE_FORMAT)
          : '-',
      },
    ],
    [
      {
        label: 'inv_PartProducerTier1',
        value: partData?.partProducer?.businessName,
      },
      {
        label: 'inv_CountryOfPartProducerTier1',
        value: countryLabels?.[partData?.partProducer?.country],
        img: countryFlagImages?.[
          countryLabels?.[partData?.partProducer?.country]
        ],
      },
    ],
    ...(include([vehicleParts?.battery], activeMenu)
      ? [
          [
            { label: 'inv_PartProducerTier2', value: partInfo?.supplier1 },
            {
              label: 'inv_CountryOfPartProducerTier2',
              value: partInfo?.supplierCountry1,
              img: countryFlagImages?.[partInfo?.supplierCountry1],
            },
          ],
        ]
      : []),
    ...(include([vehicleParts?.engine], activeMenu)
      ? [
          [
            {
              label: 'inv_ProductCategory',
              value: partInfo?.productCategory,
            },
            {
              label: 'inv_BrakePowerBP',
              value: partInfo?.brakePower,
            },
          ],
        ]
      : []),
    ...(include(
      [vehicleParts?.engine, vehicleParts?.windshield, vehicleParts?.battery],
      activeMenu,
    )
      ? [
          isArray(partInfo?.materials)
            ? partInfo?.materials
                ?.map(({ material = '', refurbisher, refurbisherCountry }) => [
                  {
                    label: getRefurbisherTitle({ material }),
                    value: refurbisher,
                    hidden: include([vehicleParts?.engine], activeMenu),
                  },
                  {
                    label: 'inv_CountryOfRefurbisher',
                    value: refurbisherCountry,
                    img: countryFlagImages?.[refurbisherCountry],
                    hidden: include([vehicleParts?.engine], activeMenu),
                  },
                ])
                ?.flat(1)
            : [],
        ]
      : []),
    ...(include(
      [vehicleParts?.engine, vehicleParts?.windshield, vehicleParts?.battery],
      activeMenu,
    )
      ? [
          isArray(partInfo?.materials)
            ? partInfo?.materials
                ?.map(({ recycler, recyclerCountry }) => [
                  {
                    label: 'user_ScrappingFacility',
                    value: recycler,
                    hidden: include([vehicleParts?.engine], activeMenu),
                  },
                  {
                    label: 'inv_CountryOfMaterialSource',
                    value: recyclerCountry,
                    img: countryFlagImages?.[recyclerCountry],
                    hidden: include([vehicleParts?.engine], activeMenu),
                  },
                ])
                ?.flat(1)
            : [],
        ]
      : []),
    ...(include(
      [
        vehicleParts?.body,
        vehicleParts?.hubFrontWheel,
        vehicleParts?.insulatorSubAssyEngineMountLH,
        vehicleParts?.bracketFRDoorBeamFR,
        vehicleParts?.flangeEGRPipe,
        vehicleParts?.plateRadiatorSupport,
        vehicleParts?.bracketWiringHarnessClamp,
        vehicleParts?.holderFuelPressureSensor,
        vehicleParts?.lockSpareWheel,
        vehicleParts?.lidFuelFillerOpeningInner,
        vehicleParts?.innerMidBracket,
        vehicleParts?.pedalBracket,
        vehicleParts?.drumBrake,
        vehicleParts?.bumper,
        vehicleParts?.frontBumper,
        vehicleParts?.backBumper,
        vehicleParts?.batteryEV,
        vehicleParts?.engineBlock,
        vehicleParts?.wiringHarness,
        vehicleParts?.evBatteryPack,
      ],
      activeMenu,
    )
      ? [
          [
            {
              label: 'user_MaterialProducer',
              value: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? 'Vardhaman'
                : partData?.materialProducer?.businessName,
            },
            {
              label: 'inv_CountryOfMaterialProducer',
              value: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? 'India'
                : countryLabels?.[partData?.materialProducer?.country],
              img: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? countryFlagImages?.['India']
                : countryFlagImages?.[
                    countryLabels?.[partData?.materialProducer?.country]
                  ],
            },
          ],
          isEqual(activeMenu, vehicleParts?.backBumper)
            ? [
                {
                  label: 'user_MaterialProducer',
                  value: 'Peeco Polytech Pvt Ltd, Sonipat',
                },
                {
                  label: 'inv_CountryOfMaterialProducer',
                  value: countryLabels?.[partData?.materialProducer?.country],
                  img: countryFlagImages?.[
                    countryLabels?.[partData?.materialProducer?.country]
                  ],
                },
              ]
            : [],
          // include(
          //   [
          //     vehicleParts?.bumper,
          //     vehicleParts?.frontBumper,
          //     vehicleParts?.backBumper,
          //   ],
          //   activeMenu,
          // )
          //   ? [
          //       {
          //         label: 'user_MaterialProducer',
          //         value: partInfo?.refurbisher2,
          //       },
          //       {
          //         label: 'inv_CountryOfMaterialProducer',
          //         value: partInfo?.refurbisherCountry2,
          //         img: countryFlagImages?.[partInfo?.refurbisherCountry],
          //       },
          //     ]
          //   : [],
          [
            {
              label: 'user_ScrappingFacility',
              value: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? 'Maruti Suzuki Toyotsu India Private Limited'
                : partData?.recycler?.businessName,
            },
            {
              label: 'inv_CountryOfScrappingFacility',
              value: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? 'India'
                : countryLabels?.[partData?.recycler?.country],
              img: isEqual(activeMenu, vehicleParts?.hubFrontWheel)
                ? countryFlagImages?.['India']
                : countryFlagImages?.[
                    countryLabels?.[partData?.recycler?.country]
                  ],
            },
          ],
          [
            {
              label: include(
                [
                  vehicleParts.bumper,
                  vehicleParts?.frontBumper,
                  vehicleParts?.backBumper,
                ],
                activeMenu,
              )
                ? 'inv_VirginPlasticProducer'
                : include(
                      [vehicleParts.engineBlock, vehicleParts?.evBatteryPack],
                      activeMenu,
                    )
                  ? 'inv_VirginMaterialProducer'
                  : 'inv_VirginIronProducer',
              value: partInfo?.virginIronProducer,
              hidden: include([vehicleParts?.batteryEV], activeMenu),
            },
            {
              label: 'inv_CountryOfVirginProducer',
              value: partInfo?.producerCountry,
              img: countryFlagImages?.[partInfo?.producerCountry],
              hidden: include([vehicleParts?.batteryEV], activeMenu),
            },
          ],
        ]
      : []),
    ...(include([vehicleParts?.battery], activeMenu)
      ? [
          [
            {
              label: 'inv_VirginLeadProducer',
              value: partInfo?.virginLeadProducer,
            },
            {
              label: 'inv_CountryOfProducer',
              value: partInfo?.producerCountry,
              img: countryFlagImages?.[partInfo?.producerCountry],
            },
          ],
          [
            {
              label: 'inv_VirginSulfuricAcidProducer',
              value: partInfo?.virginSulfuricProducer,
            },
            {
              label: 'inv_CountryOfProducer',
              value: partInfo?.producerCountry,
              img: countryFlagImages?.[partInfo?.producerCountry],
            },
          ],
        ]
      : []),
    ...(include([vehicleParts?.batteryEV], activeMenu)
      ? [
          [
            {
              label: 'inv_VirginCobaltProducer',
              value: partInfo?.virginCobaltProducer,
            },
            {
              label: 'inv_CountryOfProducer',
              value: partInfo?.producerCountry,
              img: countryFlagImages?.[partInfo?.producerCountry],
            },
          ],
        ]
      : []),
  ]

  const columns = [
    {
      title: t('inv_ServiceStationName'),
      dataIndex: 'serviceStation',
      key: 'inv_ServiceStationName',
    },
    {
      title: t('user_Address'),
      dataIndex: 'address',
      key: 'user_Address',
    },
    {
      title: t('inv_GoogleMapsLink'),
      dataIndex: 'googleMapsLink',
      key: 'inv_GoogleMapsLink',
      render: rowData => (
        <a
          href={rowData}
          className="text-underline"
          target="_blank"
          rel="noreferrer"
        >
          {t('inv_GoogleMaps')}
        </a>
      ),
    },
  ].filter(({ hidden }) => !hidden)
  const imageUrl = isEqual(activeMenu, 'frontBumper')
    ? 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQt5a8r6ZCrQJkNFTTjYZm_2E71l8nRklkMGQ&s'
    : isEqual(activeMenu, 'backBumper')
      ? 'https://boodmo.com/media/cache/catalog_part/images/parts/ab93819077bcf9cff32dde33d00235c2.webp'
      : isEqual(activeMenu, 'hubFrontWheel')
        ? hubFrontWheel
        : null

  const tableViewUI = useCallback(
    ({ info }) =>
      info?.map((arr, ind) => (
        <div className="parts-detail-row" key={ind}>
          {arr?.map(
            ({ label, value, img, hidden, colSpan }, i) =>
              !hidden && (
                <div className="parts-detail-info" key={i} colSpan={colSpan}>
                  <p>{t(label)}</p>
                  {img ? (
                    <div className="d-flex align-center">
                      <div>
                        <img className="flag-wrapper" src={img} alt="" />
                      </div>
                      <span>{value || '-'}</span>
                    </div>
                  ) : (
                    <span>{value || '-'}</span>
                  )}
                </div>
              ),
          )}
        </div>
      )),
    [],
  )

  const collectionTableUI = useCallback(
    ({ dataSource = [], columns }) => (
      <>
        <h3 className="mt-10">{t('inv_CollectionPoints')}</h3>
        <ANTDTable
          bordered
          dataSource={dataSource}
          columns={columns}
          className="collection-table"
          // pagination={false}
          pagination={false}
          size="small"
          // loading={loader}
        />
      </>
    ),
    [],
  )

  const componentDdpInfoData = ddpData => [
    { label: 'inv_PartDPPID', value: ddpData?.id },
    { label: 'inv_DPPCreated', value: ddpData?.created },
    { label: 'inv_DPPLastUpdated', value: ddpData?.updated },
    { label: 'inv_DPPInitiatedBy', value: ddpData?.initiatedBy },
  ]

  const componentInfo1 = ({ compData }) => [
    [
      { label: 'inv_PartProducerBusinessName', value: compData?.producerName },
      { label: 'inv_PartStatus', value: compData?.status },
    ],
    ...(include(['inv_EngineBlock'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherAluminium',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_CylinderHead'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherAluminium',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_VirginAluminiumProducer',
                      value: compData?.virginProducer,
                    },
                    {
                      label: 'inv_CountryOfProducer',
                      value: compData?.producerCountry,
                      img: countryFlagImages?.[compData?.producerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_Crankshaft'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherSteel',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_VirginIronProducer',
                      value: compData?.virginProducer,
                    },
                    {
                      label: 'inv_CountryOfProducer',
                      value: compData?.producerCountry,
                      img: countryFlagImages?.[compData?.producerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventorySparkPlug'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherNickel',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventoryGasket'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherRubber',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventoryExhaustSystem'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherPlastic',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_Bearings'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_RefurbisherCopper',
                      value: refurbisher,
                    },
                    {
                      label: 'inv_CountryOfRefurbisher',
                      value: refurbisherCountry,
                      img: countryFlagImages?.[refurbisherCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'user_ScrappingFacility',
                      value: recycler,
                    },
                    {
                      label: 'inv_CountryOfMaterialSource',
                      value: recyclerCountry,
                      img: countryFlagImages?.[recyclerCountry],
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
  ]
  const componentInfo2 = ({ compData }) => [
    [
      { label: 'inv_DateOfManufacture', value: compData?.manufacturingDate },
      {
        label: 'inv_ManufacturingLocation',
        value: compData?.manufacturingLocation,
      },
    ],
    [
      {
        label: 'inv_ProductCategory',
        value: compData?.productCategory,
      },
      {
        label: 'inv_Dimensions',
        value: compData?.dimensions,
      },
    ],
    ...(include(['inv_EngineBlock'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_TensileStrength',
                      value: compData?.tensileStrength,
                    },
                    {
                      label: 'inv_ThermalConductivity',
                      value: compData?.thermalConductivity,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_MaximumOperatingTemperature',
                      value: compData?.maxTemperature,
                    },
                    { label: 'job_Weight', value: compData?.weight },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_CylinderHead'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    { label: 'inv_AirflowCFM', value: compData?.airFlow },
                    {
                      label: 'inv_CompressionRatio',
                      value: compData?.compressionRatio,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_ValveLift',
                      value: compData?.valveLift,
                    },
                    {
                      label: 'inv_CoolingEfficiency',
                      value: compData?.coolingEfficiency,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_Crankshaft'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_TensileStrength',
                      value: compData?.tensileStrength,
                    },
                    {
                      label: 'inv_FatigueStrength',
                      value: compData?.fatigueStrength,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_Hardness',
                      value: compData?.hardness,
                    },
                    { label: 'job_Weight', value: compData?.weight },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventorySparkPlug'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    { label: 'inv_HeatRange', value: compData?.heatRange },
                    {
                      label: 'inv_VoltageRating',
                      value: compData?.voltageRating,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_CurrentRating',
                      value: compData?.currentRating,
                    },
                    { label: 'inv_ServiceLife', value: compData?.serviceLife },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventoryGasket'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_TemperatureResistance',
                      value: compData?.temperatureResistance,
                    },
                    {
                      label: 'inv_PressureResistance',
                      value: compData?.pressureResistance,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_CompressionSet',
                      value: compData?.compressionSet,
                    },
                    {
                      label: 'inv_TensileStrength',
                      value: compData?.tensileStrength,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_GenericInventoryExhaustSystem'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_BackPressure',
                      value: compData?.backPressure,
                    },
                    {
                      label: 'inv_NoiseReduction',
                      value: compData?.noiseReduction,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_ThermalEfficiency',
                      value: compData?.thermalEfficiency,
                    },
                    { label: 'inv_Durability', value: compData?.durability },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
    ...(include(['inv_Bearings'], compData?.title)
      ? [
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ refurbisher, refurbisherCountry }) => [
                    {
                      label: 'inv_LoadCapacity',
                      value: compData?.loadCapacity,
                    },
                    {
                      label: 'inv_SpeedRating',
                      value: compData?.speedRating,
                    },
                  ])
                  ?.flat(1)
              : []),
          ],
          [
            ...(isArray(compData?.materials)
              ? compData?.materials
                  ?.map(({ recycler, recyclerCountry }) => [
                    {
                      label: 'inv_MaximumOperatingTemperature',
                      value: compData?.operatingTemperature,
                    },
                    { label: 'inv_FatigueLife', value: compData?.fatigueLife },
                  ])
                  ?.flat(1)
              : []),
          ],
        ]
      : []),
  ]

  const partTitle = () => {
    switch (activeMenu) {
      case vehicleParts?.drumBrake:
        return `${t('inv_DrumBrake')} ${val}`

      case vehicleParts?.hubFrontWheel:
        return `${t('inv_HubFrontWheel')} ${val}`

      case vehicleParts?.insulatorSubAssyEngineMountLH:
        return `${t('job_InsulatorSubAssyEngineMountLH')} ${val}`
      case vehicleParts?.bracketFRDoorBeamFR:
        return `${t('job_BracketFRDoorBeamFR')} ${val}`
      case vehicleParts?.flangeEGRPipe:
        return `${t('job_FlangeEGRPipe')} ${val}`
      case vehicleParts?.plateRadiatorSupport:
        return `${t('job_PlateRadiatorSupport')} ${val}`
      case vehicleParts?.bracketWiringHarnessClamp:
        return `${t('job_BracketWiringHarnessClamp')} ${val}`
      case vehicleParts?.holderFuelPressureSensor:
        return `${t('job_HolderFuelPressureSensor')} ${val}`
      case vehicleParts?.lockSpareWheel:
        return `${t('job_LockSpareWheel')} ${val}`
      case vehicleParts?.lidFuelFillerOpeningInner:
        return `${t('job_LidFuelFillerOpeningInner')} ${val}`
      case vehicleParts?.innerMidBracket:
        return `${t('job_InnerMidBracket')} ${val}`
      case vehicleParts?.pedalBracket:
        return `${t('job_PedalBracket')} ${val}`

      default:
        break
    }
  }

  return (
    <>
      <h3 className="mb-20">{partTitle()}</h3>

      <div className="part-content">
        <div className="parts-qr-wrapper">
          <ANTDImage src={ddpData?.qrCode} />
        </div>
        <div className="part-detail-img">
          <img
            className="part-image-wrapper"
            src={imageUrl || partData?.ddpData?.imageUrl || noImage}
            alt="parts"
          />
        </div>
        <table>
          <tbody>
            {ddpInfoData?.map((value, index) =>
              !value?.hidden ? (
                <tr key={index} className={value?.classNames}>
                  <td>
                    <b>{t(value.label)}</b>
                  </td>
                  <td>
                    <span className="mr-15">: </span>
                    {value?.value || '-'}
                  </td>
                  {value?.icon ? <td>{value?.icon}</td> : null}
                </tr>
              ) : null,
            )}
          </tbody>
        </table>
      </div>
      <div className="part-view-info">
        <div className="part-view-box">
          {tableViewUI({ info: partSubInfo })}
        </div>
        <div className="part-view-box">
          {tableViewUI({ info: partSubInfoCol2 })}
        </div>
      </div>
      {include(
        [
          vehicleParts.engine,
          vehicleParts.body,
          vehicleParts?.hubFrontWheel,
          vehicleParts?.insulatorSubAssyEngineMountLH,
          vehicleParts?.bracketFRDoorBeamFR,
          vehicleParts?.flangeEGRPipe,
          vehicleParts?.plateRadiatorSupport,
          vehicleParts?.bracketWiringHarnessClamp,
          vehicleParts?.holderFuelPressureSensor,
          vehicleParts?.lockSpareWheel,
          vehicleParts?.lidFuelFillerOpeningInner,
          vehicleParts?.innerMidBracket,
          vehicleParts?.pedalBracket,
          vehicleParts?.drumBrake,
          vehicleParts?.bumper,
          vehicleParts?.frontBumper,
          vehicleParts?.backBumper,
          vehicleParts?.engineBlock,
          vehicleParts?.wiringHarness,
          vehicleParts?.evBatteryPack,
        ],
        activeMenu,
      ) &&
        collectionTableUI({
          dataSource: include(
            [vehicleParts?.frontBumper, vehicleParts?.backBumper],
            activeMenu,
          )
            ? [
                {
                  serviceStation: 'Maruti Suzuki Service Center',
                  address: '789, Vasant Kunj, Delhi-110070',
                  googleMapsLink:
                    'https://www.google.com/maps/place/Maruti+Suzuki+Authorised+Service+%28R.S+Automobiles%29/data=!4m7!3m6!1s0x390d1e76bedce689:0x81565df18cffc60a!8m2!3d28.5214559!4d77.1676998!16s%2Fg%2F1q2wgd9fs!19sChIJiebcvnYeDTkRCsb_jPFdVoE?authuser=0&hl=en&rclk=1',
                },
              ]
            : [],
          columns,
        })}
      {include([vehicleParts.engine], activeMenu) && (
        <>
          <div className="mt-20">
            {partInfo?.components?.map(({ ddpData, ...compData }, i) => (
              <div className="detail-wrapper mt-10" key={i}>
                <h3 className="mb-10">{t(compData?.title)}</h3>
                <div className="part-content">
                  <div className="parts-qr-wrapper">
                    <ANTDImage src={ddpData?.qrCode} />
                  </div>
                  <div className="part-detail-img">
                    <img
                      className="part-image-wrapper"
                      src={ddpData?.imageUrl || noImage}
                      alt="parts"
                    />
                  </div>
                  <table>
                    <tbody>
                      {componentDdpInfoData(ddpData)?.map((value, index) =>
                        !value?.hidden ? (
                          <tr key={index} className={value?.classNames}>
                            <td>
                              <b>{t(value.label)}</b>
                            </td>
                            <td>
                              <span className="mr-15">: </span>
                              {value?.value || '-'}
                            </td>
                            {value?.icon ? <td>{value?.icon}</td> : null}
                          </tr>
                        ) : null,
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="part-view-info">
                  <div className="part-view-box">
                    {tableViewUI({ info: componentInfo1({ compData }) })}
                  </div>
                  <div className="part-view-box">
                    {tableViewUI({ info: componentInfo2({ compData }) })}
                  </div>
                </div>
                {collectionTableUI({
                  dataSource: compData?.collectionDataSource,
                  columns,
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

export default PartDetails
